import { type ClassValue, clsx } from "clsx";
import { ContentState, convertFromHTML, EditorState } from "draft-js";
import htmlToDraft from "html-to-draftjs";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const capitalizeFirstLetter = (string: string) => {
  return string ? string.charAt(0).toUpperCase() + string.slice(1) : "";
};

export const formatTimeSpent = (startDate: string): string => {
  const start = new Date(startDate);
  const now = new Date();

  const diff = now.getTime() - start.getTime();

  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const years = Math.floor(diff / (1000 * 60 * 60 * 24 * 365));

  if (years > 0) {
    return `${years} Jahr${years > 1 ? "e" : ""}`;
  } else if (days > 0) {
    return `${days} Tag${days > 1 ? "e" : ""}`;
  } else if (hours > 0) {
    return `${hours} Std.${hours > 1 ? "" : ""}`;
  } else {
    return `${minutes} Minute${minutes > 1 ? "s" : ""}`;
  }
};

export const formatDateDetail = (dateString: string): string => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    // Handle invalid date
    return "Invalid Date";
  }
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
  };
  return date.toLocaleDateString("de", options);
};

export const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    // Handle invalid date
    return "Invalid Date";
  }
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
  };
  return date.toLocaleDateString("de", options);
};

export const formatDateTimeShort = (dateString: string): string => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    // Handle invalid date
    return "Invalid Date";
  }
  return date.toLocaleDateString("de", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatCurrency = (price: number) => {
  return (
    price.toLocaleString("de-DE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + " €"
  );
};

export const downloadURI = (uri: string, name: string) => {
  var link = document.createElement("a");
  link.download = name;
  link.href = uri;
  link.target = "_blank";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const parseRoles = (role: string) => {
  switch (role) {
    case "ADMIN":
      return "Administrator";
    case "USER":
      return "Mitarbeiter";
    case "SUPER_ADMIN":
      return "Super Administrator";
    default:
      return role;
  }
};

export const convertHtmlToEditorState = (html: string): EditorState => {
  let processedHtml;
  if (html.includes("<p>")) {
    processedHtml = html
      .replaceAll("&#8205;", "")
      .replaceAll(`\n<p></p>\n`, "<p>&#8205;</p>");
  } else {
    processedHtml = html
      .replaceAll("&#8205;", "")
      .replaceAll(`\n\n`, "<p>&#8205;</p>");
    if (processedHtml.includes("\n")) {
      let finalHtml = "";
      processedHtml.split("\n").forEach((line) => {
        finalHtml += `<p>${line}</p>`;
      });
      processedHtml = finalHtml;
    }
  }
  const blocksFromHTML = convertFromHTML(processedHtml);
  const contentState = ContentState.createFromBlockArray(
    blocksFromHTML.contentBlocks,
    blocksFromHTML.entityMap
  );
  return EditorState.createWithContent(contentState);
};

export const convertHtmlToContentState = (html: string) => {
  const contentBlock = htmlToDraft(html);
  if (contentBlock) {
    return ContentState.createFromBlockArray(contentBlock.contentBlocks);
  }
  return ContentState.createFromText("");
};

export const formatPrice = (price: number) => {
  return price
    ? price.toLocaleString("de-DE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }) + " €"
    : "-";
};

export const parseGermanNumber = (value: string): number => {
  if (!value) return 0;
  const normalizedValue = value.replace(/\./g, "").replace(",", ".");
  return parseFloat(normalizedValue) || 0;
};

export const germanToBackendNumber = (value: string): number => {
  if (!value) return 0;
  const cleanValue = value.replace(/[€\s]/g, "");
  const normalizedValue = cleanValue.replace(/\./g, "").replace(",", ".");
  return parseFloat(normalizedValue) || 0;
};
